import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePreventScroll from '../hooks/usePreventScroll';
import AuthModal from './AuthModal';
import UserProfile from './UserProfile';
import YCodeThumbnail from './YCodeThumbnail';

const Navbar = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [questionIDETheme, setQuestionIDETheme] = useState(() => {
    return localStorage.getItem('questionIDE-theme') || 'dark';
  });
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  
  const { user, isAuthenticated, logout } = useAuth();
  
  // Prevent background scroll when mobile menu is open
  usePreventScroll(showMobileMenu);

  // Check if we're on the admin panel page
  const isAdminPanel = location.pathname === '/admin';
  const isGhostAdminPanel = location.pathname === '/admin/recordings';
  const isDashboard = location.pathname === '/dashboard';
  const isCourseDetail = location.pathname.startsWith('/courses/') && location.pathname !== '/courses';
  const isQuestionIDE = location.pathname === '/question-ide' || location.search.includes('questionId');
  
  // Listen for theme changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'questionIDE-theme') {
        setQuestionIDETheme(e.newValue || 'dark');
      }
    };
    
    // Listen for storage events (when theme changes in another tab/window)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes (for same-tab updates)
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem('questionIDE-theme') || 'dark';
      if (currentTheme !== questionIDETheme) {
        setQuestionIDETheme(currentTheme);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [questionIDETheme]);
  
  // Helper variable for pages with light backgrounds
  // For QuestionIDE, check the theme - if dark, use dark navbar styling
  const isLightBgPage = isAdminPanel || isGhostAdminPanel || isDashboard || isCourseDetail || (isQuestionIDE && questionIDETheme === 'light');
  const isDarkNavbar = isQuestionIDE && questionIDETheme === 'dark';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/courses', label: 'Courses' },
    // { path: '/articles', label: 'Articles' },
    { path: '/question-packages', label: 'Question Packages' },
    { path: '/ide', label: 'IDE' },
    { path: '/ai-assistant', label: 'AI Assistant' },
    { path: '/interview-simulator', label: 'AI Interviews' },
    { path: '/dsa-crash-course', label: 'DSA Crash Course' },
    { path: '/about', label: 'About' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
      isDarkNavbar
        ? 'bg-gray-800 shadow-lg border-b border-gray-700'
        : isLightBgPage
          ? 'bg-white shadow-lg border-b border-gray-200'
          : isScrolled 
            ? 'bg-white/20 backdrop-blur-xl shadow-2xl border-b border-white/20'
            : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center group"
              onClick={() => {
                // Force scroll to top when clicking logo
                setTimeout(() => {
                  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                }, 50);
              }}
            >
              <div className="transform group-hover:scale-110 transition-transform duration-300">
                <YCodeThumbnail size="small" />
              </div>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  // Force scroll to top after navigation (works on both mobile and desktop)
                  setTimeout(() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;
                  }, 50);
                }}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isDarkNavbar
                    ? isActiveRoute(link.path)
                      ? 'text-purple-400 bg-purple-900/30 border border-purple-700'
                      : 'text-gray-300 hover:text-purple-400 hover:bg-purple-900/20'
                    : isLightBgPage
                      ? isActiveRoute(link.path)
                        ? 'text-blue-600 bg-blue-50 border border-blue-200'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                      : isActiveRoute(link.path)
                        ? 'text-white bg-white/20 backdrop-blur-sm'
                        : 'text-gray-200 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
                {isActiveRoute(link.path) && (
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${
                    isDarkNavbar
                      ? 'bg-purple-400'
                      : isLightBgPage
                        ? 'bg-blue-600'
                        : 'bg-gradient-to-r from-teal-400 to-cyan-400'
                  }`}></div>
                )}
              </Link>
            ))}
            
            {user?.role === 'ADMIN' && (
              <Link 
                to="/admin" 
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isDarkNavbar
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20 border border-red-700'
                    : isLightBgPage
                      ? 'text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200'
                      : 'text-red-300 hover:text-red-200 hover:bg-red-500/20'
                }`}
              >
                <span className="flex items-center">
                  <span className="mr-2">⚡</span>
                  Admin Panel
                </span>
              </Link>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {isAuthenticated() ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center space-x-2 sm:space-x-3 px-2 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    isDarkNavbar
                      ? 'text-gray-300 hover:bg-gray-700 border border-gray-600'
                      : isLightBgPage
                        ? 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                        : 'text-white hover:bg-white/20 backdrop-blur-sm'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm bg-gradient-to-r from-blue-500 to-purple-600">
                    {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden sm:block">{user?.firstName || user?.username}</span>
                  <div className={`hidden sm:block transform transition-transform duration-300 ${
                    isDarkNavbar ? 'text-gray-400' : isLightBgPage ? 'text-gray-500' : 'text-white'
                  }`}>
                    ▼
                  </div>
                </button>
                
                {showUserMenu && (
                  <div className={`absolute right-0 mt-3 w-64 rounded-xl shadow-xl py-2 animate-fade-in-up border ${
                    isDarkNavbar 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-200'
                  }`} style={{ zIndex: 9999 }}>
                    <div className={`px-4 py-3 border-b ${
                      isDarkNavbar ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                      <div className={`font-semibold ${
                        isDarkNavbar ? 'text-white' : 'text-gray-900'
                      }`}>{user?.firstName} {user?.lastName}</div>
                      <div className={`text-sm ${
                        isDarkNavbar ? 'text-gray-400' : 'text-gray-500'
                      }`}>{user?.email}</div>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user?.role === 'ADMIN' 
                            ? isDarkNavbar
                              ? 'bg-red-900/30 text-red-400'
                              : 'bg-red-100 text-red-800'
                            : isDarkNavbar
                              ? 'bg-blue-900/30 text-blue-400'
                              : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user?.role}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserProfile(true);
                        setShowUserMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 flex items-center ${
                        isDarkNavbar
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-3">👤</span>
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        navigate('/dashboard');
                        setShowUserMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 flex items-center ${
                        isDarkNavbar
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-3">📊</span>
                      Dashboard
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 flex items-center ${
                        isDarkNavbar
                          ? 'text-red-400 hover:bg-red-900/20'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <span className="mr-3">🚪</span>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className={`hidden md:inline-flex px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isDarkNavbar
                    ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 border border-purple-700'
                    : isLightBgPage
                      ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transform hover:scale-105 shadow-lg'
                }`}
              >
                Sign In
              </button>
            )}
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className={`p-2 rounded-lg transition-colors duration-200 relative z-[101] ${
                  isDarkNavbar
                    ? 'text-gray-300 hover:bg-gray-700'
                    : isLightBgPage
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/20'
                }`}
              >
                {showMobileMenu ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <>
          {/* Overlay */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998
            }}
            className="md:hidden"
            onClick={() => setShowMobileMenu(false)}
          />
          
          {/* Menu Panel */}
          <div 
            ref={mobileMenuRef}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '80vw',
              maxWidth: '320px',
              height: '100vh',
              backgroundColor: isDarkNavbar ? '#1f2937' : 'white',
              boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
              zIndex: 9999,
              overflowY: 'auto'
            }}
            className="md:hidden"
          >
            {/* Close Button */}
            <div className={`sticky top-0 flex justify-end p-4 border-b z-10 ${
              isDarkNavbar ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <button
                onClick={() => setShowMobileMenu(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkNavbar 
                    ? 'hover:bg-gray-700 text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
            <div className="p-6">
              {/* User Info */}
              {isAuthenticated() ? (
                <div className={`mb-6 pb-6 border-b ${
                  isDarkNavbar ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold bg-gradient-to-r from-blue-500 to-purple-600">
                      {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div className={`font-semibold ${
                        isDarkNavbar ? 'text-white' : 'text-gray-900'
                      }`}>{user?.firstName} {user?.lastName}</div>
                      <div className={`text-sm ${
                        isDarkNavbar ? 'text-gray-400' : 'text-gray-500'
                      }`}>{user?.email}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user?.role === 'ADMIN' 
                      ? isDarkNavbar
                        ? 'bg-red-900/30 text-red-400'
                        : 'bg-red-100 text-red-800'
                      : isDarkNavbar
                        ? 'bg-blue-900/30 text-blue-400'
                        : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user?.role}
                  </span>
                </div>
              ) : (
                <div className={`mb-6 pb-6 border-b ${
                  isDarkNavbar ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <button
                    onClick={() => {
                      setShowAuthModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Sign In
                  </button>
                </div>
              )}

              {/* Navigation Links */}
              <nav className="space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => {
                      setShowMobileMenu(false);
                      // Force scroll to top on mobile after navigation
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                      }, 100);
                    }}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                      isDarkNavbar
                        ? isActiveRoute(link.path)
                          ? 'text-purple-400 bg-purple-900/30 border border-purple-700'
                          : 'text-gray-300 hover:text-purple-400 hover:bg-purple-900/20'
                        : isActiveRoute(link.path)
                          ? 'text-blue-600 bg-blue-50 border border-blue-200'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {user?.role === 'ADMIN' && (
                  <Link 
                    to="/admin"
                    onClick={() => {
                      setShowMobileMenu(false);
                      // Force scroll to top on mobile after navigation
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                      }, 100);
                    }}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                      isDarkNavbar
                        ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20 border border-red-700'
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200'
                    }`}
                  >
                    <span className="flex items-center">
                      <span className="mr-2">⚡</span>
                      Admin Panel
                    </span>
                  </Link>
                )}
              </nav>

              {/* User Actions */}
              {isAuthenticated() && (
                <div className={`mt-6 pt-6 border-t space-y-2 ${
                  isDarkNavbar ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <button
                    onClick={() => {
                      setShowUserProfile(true);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 flex items-center ${
                      isDarkNavbar
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-3">👤</span>
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      setShowMobileMenu(false);
                      // Force scroll to top on mobile after navigation
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                      }, 100);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 flex items-center ${
                      isDarkNavbar
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-3">📊</span>
                    Dashboard
                  </button>
                  <button
                    onClick={() => {
                      logout();
                      setShowMobileMenu(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 flex items-center ${
                      isDarkNavbar
                        ? 'text-red-400 hover:bg-red-900/20'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <span className="mr-3">🚪</span>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Modals */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
      <UserProfile 
        isOpen={showUserProfile} 
        onClose={() => setShowUserProfile(false)} 
      />
    </nav>
  );
};

export default Navbar; 
